import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface AuditLogEntry {
  timestamp: string;
  userEmail: string;
  userName: string;
  action: string;
  details: string;
  ipAddress?: string;
}

export default function AdminAuditLog() {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/admin/audit-log?limit=200');
      const data = await response.json();

      if (data.success) {
        setLogs(data.data);
      } else {
        setError(data.error || 'Failed to load logs');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (iso: string) => {
    const date = new Date(iso);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getActionIcon = (action: string) => {
    if (action.includes('terminal')) return '💻';
    if (action.includes('login')) return '🔐';
    if (action.includes('settings')) return '⚙️';
    return '📝';
  };

  return (
    <div className="admin-page">
      <Link to="/" className="back-link">← Back to Dashboard</Link>
      <h2>Audit Log</h2>

      <p className="admin-description">
        Security audit trail for admin actions. Shows the last 200 events.
      </p>

      <div className="audit-controls">
        <button className="button-secondary" onClick={fetchLogs} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="audit-error">
          {error}
        </div>
      )}

      {!loading && !error && logs.length === 0 && (
        <div className="audit-empty">
          No audit events recorded yet.
        </div>
      )}

      {logs.length > 0 && (
        <div className="audit-log-container">
          <table className="audit-log-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>User</th>
                <th>Action</th>
                <th>Details</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, index) => (
                <tr key={index}>
                  <td className="audit-time">{formatTimestamp(log.timestamp)}</td>
                  <td className="audit-user">
                    <span className="audit-user-name">{log.userName}</span>
                    <span className="audit-user-email">{log.userEmail}</span>
                  </td>
                  <td className="audit-action">
                    <span className="audit-action-icon">{getActionIcon(log.action)}</span>
                    {log.action}
                  </td>
                  <td className="audit-details">{log.details}</td>
                  <td className="audit-ip">{log.ipAddress || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        .audit-controls {
          margin-bottom: 1rem;
        }

        .audit-error {
          background: var(--accent-red);
          color: white;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1rem;
        }

        .audit-empty {
          text-align: center;
          padding: 2rem;
          color: var(--text-secondary);
        }

        .audit-log-container {
          overflow-x: auto;
          border-radius: 8px;
          border: 1px solid var(--border-color);
        }

        .audit-log-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.9rem;
        }

        .audit-log-table th,
        .audit-log-table td {
          padding: 0.75rem 1rem;
          text-align: left;
          border-bottom: 1px solid var(--border-color);
        }

        .audit-log-table th {
          background: var(--bg-secondary);
          font-weight: 600;
          color: var(--text-secondary);
          text-transform: uppercase;
          font-size: 0.75rem;
          letter-spacing: 0.05em;
        }

        .audit-log-table tbody tr:hover {
          background: var(--bg-secondary);
        }

        .audit-time {
          white-space: nowrap;
          color: var(--text-secondary);
          font-family: monospace;
        }

        .audit-user {
          display: flex;
          flex-direction: column;
          gap: 0.125rem;
        }

        .audit-user-name {
          font-weight: 500;
        }

        .audit-user-email {
          font-size: 0.75rem;
          color: var(--text-secondary);
        }

        .audit-action {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .audit-action-icon {
          font-size: 1rem;
        }

        .audit-details {
          max-width: 300px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .audit-ip {
          font-family: monospace;
          font-size: 0.8rem;
          color: var(--text-secondary);
        }
      `}</style>
    </div>
  );
}
